import { type FC, useContext } from "react";
import { Link } from "react-router-dom";
import { PageTitle } from "../../../components/PageTitle";
import { AuthContext } from "../../../hooks/authProvider/authProvider";
import {
  useOrganizationsRestApi,
  useUnassignedUsersRestApi,
} from "../../../hooks/useOrganizationsRestApi";

export const AdminDashboardPage: FC = () => {
  const { currentUser } = useContext(AuthContext);

  // 統計情報取得
  const { data: orgData, loading: orgLoading } = useOrganizationsRestApi();
  const { data: unassignedData, loading: unassignedLoading } =
    useUnassignedUsersRestApi();

  const dashboardItems = [
    {
      title: "組織管理",
      description: "組織の作成、編集、削除を行います",
      path: "/admin/organizations",
      icon: "🏢",
      color: "bg-blue-500 hover:bg-blue-600",
    },
    {
      title: "ユーザー管理",
      description: "ユーザーの管理を行います",
      path: "/admin/users",
      icon: "👥",
      color: "bg-green-500 hover:bg-green-600",
    },
    {
      title: "システム設定",
      description: "システム全体の設定を管理します",
      path: "/admin/settings",
      icon: "⚙️",
      color: "bg-purple-500 hover:bg-purple-600",
      disabled: true,
    },
  ];

  return (
    <div className="container mx-auto px-4 py-6">
      <PageTitle>管理者ダッシュボード</PageTitle>

      <div className="mb-8">
        <div className="bg-white rounded-lg shadow p-6">
          <h2 className="text-xl font-semibold text-gray-800 mb-2">
            ようこそ、{currentUser?.familyName} {currentUser?.givenName}さん
          </h2>
          <p className="text-gray-600">
            管理者権限でシステムの管理を行うことができます。
          </p>
        </div>
      </div>

      {/* 統計情報 */}
      <div className="mb-8">
        <h3 className="text-lg font-semibold text-gray-800 mb-4">
          システム統計
        </h3>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="bg-white rounded-lg shadow p-6">
            <div className="flex items-center">
              <div className="p-2 bg-blue-100 rounded-lg">
                <svg
                  className="w-6 h-6 text-blue-600"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4"
                  />
                </svg>
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-600">組織数</p>
                <p className="text-2xl font-bold text-gray-900">
                  {orgLoading ? "..." : orgData?.organizations?.length || 0}
                </p>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-lg shadow p-6">
            <div className="flex items-center">
              <div className="p-2 bg-green-100 rounded-lg">
                <svg
                  className="w-6 h-6 text-green-600"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197m13.5-.5a2.5 2.5 0 11-5 0 2.5 2.5 0 015 0z"
                  />
                </svg>
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-600">
                  アクティブ組織
                </p>
                <p className="text-2xl font-bold text-gray-900">
                  {orgLoading
                    ? "..."
                    : orgData?.organizations?.filter((org: any) => org.isActive)
                        .length || 0}
                </p>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-lg shadow p-6">
            <div className="flex items-center">
              <div className="p-2 bg-purple-100 rounded-lg">
                <svg
                  className="w-6 h-6 text-purple-600"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z"
                  />
                </svg>
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-600">
                  総ユーザー数
                </p>
                <p className="text-2xl font-bold text-gray-900">
                  {orgLoading || unassignedLoading
                    ? "..."
                    : (orgData?.organizations?.reduce(
                        (sum: number, org: any) =>
                          sum + (org.employeeCount || 0),
                        0,
                      ) || 0) + (unassignedData?.unassignedUsers?.length || 0)}
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {dashboardItems.map(item => (
          <div
            key={item.path}
            className="bg-white rounded-lg shadow-md overflow-hidden"
          >
            {item.disabled ? (
              <div className="p-6 opacity-50 cursor-not-allowed">
                <div className="flex items-center mb-4">
                  <span className="text-3xl mr-3">{item.icon}</span>
                  <h3 className="text-lg font-semibold text-gray-800">
                    {item.title}
                  </h3>
                </div>
                <p className="text-gray-600 mb-4">{item.description}</p>
                <span className="text-sm text-gray-400">近日公開予定</span>
              </div>
            ) : (
              <Link
                to={item.path}
                className="block p-6 hover:bg-gray-50 transition-colors"
              >
                <div className="flex items-center mb-4">
                  <span className="text-3xl mr-3">{item.icon}</span>
                  <h3 className="text-lg font-semibold text-gray-800">
                    {item.title}
                  </h3>
                </div>
                <p className="text-gray-600 mb-4">{item.description}</p>
                <div
                  className={`inline-flex items-center px-4 py-2 rounded-md text-white text-sm font-medium ${item.color} transition-colors`}
                >
                  管理画面へ
                  <svg
                    className="ml-2 w-4 h-4"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M9 5l7 7-7 7"
                    />
                  </svg>
                </div>
              </Link>
            )}
          </div>
        ))}
      </div>

      <div className="mt-8">
        <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
          <div className="flex">
            <div className="flex-shrink-0">
              <svg
                className="h-5 w-5 text-yellow-400"
                viewBox="0 0 20 20"
                fill="currentColor"
              >
                <path
                  fillRule="evenodd"
                  d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z"
                  clipRule="evenodd"
                />
              </svg>
            </div>
            <div className="ml-3">
              <h3 className="text-sm font-medium text-yellow-800">
                重要な注意事項
              </h3>
              <div className="mt-2 text-sm text-yellow-700">
                <p>
                  管理者機能は慎重に使用してください。組織やユーザーの削除は元に戻すことができません。
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
